<template>
  <div class="avatar-crop-uploader">
    <div class="avatar-circle" :class="{ editable: editable }" @click="onAvatarClick">
      <img :src="displayUrl" alt="头像" @error="onImgError" />
      <div v-if="editable" class="avatar-mask">更换头像</div>
    </div>

    <el-dialog title="裁剪头像" :visible.sync="dialogVisible" width="520px" append-to-body custom-class="avatar-crop-dialog"
      @closed="onDialogClosed">
      <div class="crop-body">
        <div v-if="!previewUrl" class="pick-area">
          <el-button type="primary" size="small" @click="triggerPick">选择图片</el-button>
          <p class="tip">支持 JPG / PNG / WebP，大小不超过 5MB</p>
        </div>
        <div v-else class="crop-stage-wrap">
          <div class="crop-stage" ref="stage" @mousedown.prevent="onDragStart" @touchstart.prevent="onTouchStart"
            @wheel.prevent="onWheel">
            <img ref="cropImg" class="crop-img" :src="previewUrl" :style="imgStyle" draggable="false"
              @load="onImgLoad" />
            <div class="crop-viewport"></div>
          </div>
          <div class="zoom-row">
            <span>缩放</span>
            <el-slider v-model="scalePercent" :min="100" :max="300" :show-tooltip="false"
              @input="onScaleSlider"></el-slider>
            <span class="zoom-val">{{ scalePercent }}%</span>
          </div>
        </div>
        <input ref="fileInput" type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          class="hidden-input" @change="onFileChange" />
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="uploading" :disabled="!previewUrl"
          @click="onConfirm">使用</el-button>
      </div>
    </el-dialog>

    <!-- 复用现有 OSS 分片上传 -->
    <div v-if="uploadFile" class="oss-hide">
      <oss-uploader-file :file="uploadFile" :list="true" @closeMain="onOssDone" />
    </div>
  </div>
</template>

<script>
  import OssUploaderFile from '@/common/ossuploader/oss-file.vue';

  const VIEW_SIZE = 280;
  const EXPORT_SIZE = 512;
  const MAX_SIZE = 5 * 1024 * 1024;

  export default {
    name: 'AvatarCropUploader',
    components: { OssUploaderFile },
    props: {
      editable: { type: Boolean, default: false },
      avatarUrl: { type: String, default: '' },
      sex: { type: [Number, String], default: 1 },
    },
    data() {
      return {
        dialogVisible: false,
        previewUrl: '',
        loadFailed: false,
        naturalW: 0,
        naturalH: 0,
        baseScale: 1,
        scale: 1,
        scalePercent: 100,
        offsetX: 0,
        offsetY: 0,
        dragging: false,
        dragStartX: 0,
        dragStartY: 0,
        originOffsetX: 0,
        originOffsetY: 0,
        pinchStartDist: 0,
        pinchStartScale: 1,
        uploading: false,
        uploadFile: null,
      };
    },
    computed: {
      defaultAvatarSrc() {
        return this.$store.getters.avatarSrcBySex(this.sex);
      },
      displayUrl() {
        // 地址无效（如返回 XML）时走性别默认头像
        if (this.loadFailed || !this.avatarUrl) return this.defaultAvatarSrc;
        return this.avatarUrl;
      },
      imgStyle() {
        const w = this.naturalW * this.baseScale * this.scale;
        const h = this.naturalH * this.baseScale * this.scale;
        return {
          width: `${w}px`,
          height: `${h}px`,
          transform: `translate(${this.offsetX}px, ${this.offsetY}px)`,
        };
      },
    },
    watch: {
      avatarUrl() {
        this.loadFailed = false;
      },
    },
    methods: {
      onImgError() {
        // 下载接口返回 XML/非图片时也会触发，回退性别默认头像
        this.loadFailed = true;
      },
      onAvatarClick() {
        if (!this.editable) return;
        this.dialogVisible = true;
      },
      triggerPick() {
        this.$refs.fileInput && this.$refs.fileInput.click();
      },
      onFileChange(e) {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const okType =
          /image\/(jpeg|png|webp)/i.test(file.type) ||
          /\.(jpe?g|png|webp)$/i.test(file.name);
        if (!okType) {
          this.$message.warning('仅支持 JPG / PNG / WebP 格式');
          return;
        }
        if (file.size > MAX_SIZE) {
          this.$message.warning('图片大小不能超过 5MB');
          return;
        }
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = URL.createObjectURL(file);
        this.scale = 1;
        this.scalePercent = 100;
        this.offsetX = 0;
        this.offsetY = 0;
      },
      onImgLoad() {
        const img = this.$refs.cropImg;
        if (!img) return;
        this.naturalW = img.naturalWidth;
        this.naturalH = img.naturalHeight;
        // 短边铺满视口
        this.baseScale = VIEW_SIZE / Math.min(this.naturalW, this.naturalH);
        this.centerImage();
      },
      centerImage() {
        const w = this.naturalW * this.baseScale * this.scale;
        const h = this.naturalH * this.baseScale * this.scale;
        this.offsetX = (VIEW_SIZE - w) / 2;
        this.offsetY = (VIEW_SIZE - h) / 2;
      },
      onScaleSlider(val) {
        const next = val / 100;
        this.applyScale(next);
      },
      applyScale(nextScale) {
        const old = this.scale;
        this.scale = Math.min(3, Math.max(1, nextScale));
        this.scalePercent = Math.round(this.scale * 100);
        // 以视口中心为锚点缩放
        const cx = VIEW_SIZE / 2;
        const cy = VIEW_SIZE / 2;
        const ratio = this.scale / old;
        this.offsetX = cx - (cx - this.offsetX) * ratio;
        this.offsetY = cy - (cy - this.offsetY) * ratio;
        this.clampOffset();
      },
      clampOffset() {
        const w = this.naturalW * this.baseScale * this.scale;
        const h = this.naturalH * this.baseScale * this.scale;
        if (w <= VIEW_SIZE) this.offsetX = (VIEW_SIZE - w) / 2;
        else {
          this.offsetX = Math.min(0, Math.max(VIEW_SIZE - w, this.offsetX));
        }
        if (h <= VIEW_SIZE) this.offsetY = (VIEW_SIZE - h) / 2;
        else {
          this.offsetY = Math.min(0, Math.max(VIEW_SIZE - h, this.offsetY));
        }
      },
      onDragStart(e) {
        this.dragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.originOffsetX = this.offsetX;
        this.originOffsetY = this.offsetY;
        window.addEventListener('mousemove', this.onDragMove);
        window.addEventListener('mouseup', this.onDragEnd);
      },
      onDragMove(e) {
        if (!this.dragging) return;
        this.offsetX = this.originOffsetX + (e.clientX - this.dragStartX);
        this.offsetY = this.originOffsetY + (e.clientY - this.dragStartY);
        this.clampOffset();
      },
      onDragEnd() {
        this.dragging = false;
        window.removeEventListener('mousemove', this.onDragMove);
        window.removeEventListener('mouseup', this.onDragEnd);
      },
      onTouchStart(e) {
        if (e.touches.length === 1) {
          const t = e.touches[0];
          this.dragging = true;
          this.dragStartX = t.clientX;
          this.dragStartY = t.clientY;
          this.originOffsetX = this.offsetX;
          this.originOffsetY = this.offsetY;
          window.addEventListener('touchmove', this.onTouchMove, { passive: false });
          window.addEventListener('touchend', this.onTouchEnd);
        } else if (e.touches.length === 2) {
          this.dragging = false;
          this.pinchStartDist = this.touchDist(e.touches);
          this.pinchStartScale = this.scale;
        }
      },
      onTouchMove(e) {
        e.preventDefault();
        if (e.touches.length === 1 && this.dragging) {
          const t = e.touches[0];
          this.offsetX = this.originOffsetX + (t.clientX - this.dragStartX);
          this.offsetY = this.originOffsetY + (t.clientY - this.dragStartY);
          this.clampOffset();
        } else if (e.touches.length === 2) {
          const dist = this.touchDist(e.touches);
          if (!this.pinchStartDist) return;
          this.applyScale(this.pinchStartScale * (dist / this.pinchStartDist));
        }
      },
      onTouchEnd() {
        this.dragging = false;
        this.pinchStartDist = 0;
        window.removeEventListener('touchmove', this.onTouchMove);
        window.removeEventListener('touchend', this.onTouchEnd);
      },
      touchDist(touches) {
        const a = touches[0];
        const b = touches[1];
        const dx = a.clientX - b.clientX;
        const dy = a.clientY - b.clientY;
        return Math.sqrt(dx * dx + dy * dy);
      },
      onWheel(e) {
        const delta = e.deltaY > 0 ? -0.05 : 0.05;
        this.applyScale(this.scale + delta);
      },
      exportCanvasBlob() {
        return new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          canvas.width = EXPORT_SIZE;
          canvas.height = EXPORT_SIZE;
          const ctx = canvas.getContext('2d');
          const img = this.$refs.cropImg;
          if (!img) return reject(new Error('no image'));

          const ratio = EXPORT_SIZE / VIEW_SIZE;
          const drawW = this.naturalW * this.baseScale * this.scale * ratio;
          const drawH = this.naturalH * this.baseScale * this.scale * ratio;
          const dx = this.offsetX * ratio;
          const dy = this.offsetY * ratio;

          ctx.clearRect(0, 0, EXPORT_SIZE, EXPORT_SIZE);
          ctx.save();
          ctx.beginPath();
          ctx.arc(EXPORT_SIZE / 2, EXPORT_SIZE / 2, EXPORT_SIZE / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, dx, dy, drawW, drawH);
          ctx.restore();

          canvas.toBlob(
            (blob) => {
              if (!blob) reject(new Error('toBlob failed'));
              else resolve(blob);
            },
            'image/jpeg',
            0.92
          );
        });
      },
      async onConfirm() {
        if (!this.previewUrl || this.uploading) return;
        this.uploading = true;
        try {
          const blob = await this.exportCanvasBlob();
          const file = new File([blob], `avatar_${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          this.uploadFile = {
            name: file.name,
            size: file.size,
            raw: file,
          };
        } catch (err) {
          console.error(err);
          this.uploading = false;
          this.$message.error('裁剪失败，请重试');
        }
      },
      onOssDone(data) {
        const md5 = data && (data.filemd5 || data.identifier);
        this.uploading = false;
        this.uploadFile = null;
        if (!md5) {
          this.$message.error('头像上传失败');
          return;
        }
        this.$emit('uploaded', md5);
        this.dialogVisible = false;
      },
      onDialogClosed() {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = '';
        this.uploadFile = null;
        this.uploading = false;
        this.onDragEnd();
        this.onTouchEnd();
      },
    },
    beforeDestroy() {
      this.onDialogClosed();
    },
  };
</script>

<style lang="less" scoped>
  .avatar-crop-uploader {
    display: inline-block;
  }

  .avatar-circle {
    position: relative;
    width: 88px;
    height: 88px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(36, 155, 253, 0.18);
    background: #f0f4f8;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    &.editable {
      cursor: pointer;

      &:hover .avatar-mask {
        opacity: 1;
      }
    }

    .avatar-mask {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.45);
      color: #fff;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;
    }
  }

  .hidden-input {
    display: none;
  }

  .oss-hide {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
  }

  .crop-body {
    min-height: 320px;
  }

  .pick-area {
    text-align: center;
    padding: 80px 0;

    .tip {
      margin-top: 12px;
      color: #909399;
      font-size: 12px;
    }
  }

  .crop-stage-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .crop-stage {
    position: relative;
    width: 280px;
    height: 280px;
    overflow: hidden;
    background: #1a1a1a;
    border-radius: 8px;
    cursor: grab;
    user-select: none;
    touch-action: none;

    .crop-img {
      position: absolute;
      left: 0;
      top: 0;
      will-change: transform;
    }

    .crop-viewport {
      position: absolute;
      left: 0;
      top: 0;
      width: 280px;
      height: 280px;
      border-radius: 50%;
      box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
      pointer-events: none;
      border: 1px solid rgba(255, 255, 255, 0.35);
    }
  }

  .zoom-row {
    display: flex;
    align-items: center;
    width: 280px;
    margin-top: 16px;
    gap: 10px;
    font-size: 13px;
    color: #606266;

    .el-slider {
      flex: 1;
    }

    .zoom-val {
      width: 48px;
      text-align: right;
    }
  }
</style>
